import {scrollToElement} from '../Utility/ScrollUtility';
import {checkMailValidity} from '../Utility/FormUtility';
import {LoadingAnimation} from '../Components/LoadingAnimation';
import GLightbox from 'glightbox';

export class NewsletterController {
  private readonly formElement: HTMLFormElement | null = document.querySelector('.js-newsletter-form');
  private readonly toggleStagesElement: HTMLElement | null = document.querySelector('.js-newsletter-toggle');
  private readonly stage1Element: HTMLElement | null = document.querySelector('.js-newsletter-stage-1');
  private readonly stage2Element: HTMLElement | null = document.querySelector('.js-newsletter-stage-2');
  private readonly stage3Element: HTMLElement | null = document.querySelector('.js-newsletter-stage-3');
  private readonly successContentElement: HTMLElement | null = document.querySelector('.js-newsletter-success');
  private readonly fakeEmailElement: HTMLInputElement | null = document.querySelector('.js-newsletter-fakeEmail');
  private readonly emailElement: HTMLInputElement | null = document.querySelector('.js-newsletter-email');
  private readonly hpElement: HTMLInputElement | null = document.querySelector('.js-newsletter-hp');
  private readonly firstnameElement: HTMLInputElement | null = document.querySelector('.js-newsletter-firstname');
  private readonly lastnameElement: HTMLInputElement | null = document.querySelector('.js-newsletter-lastname');
  private readonly countryElement: HTMLInputElement | null = document.querySelector('.js-newsletter-country');
  private readonly captchaImageElement: HTMLInputElement | null = document.querySelector('.js-captchaImage');
  private readonly captchaElement: HTMLInputElement | null = document.querySelector('.js-newsletter-captcha');
  private readonly sendButtonElement: HTMLInputElement | null = document.querySelector('.js-newsletter-send');
  private readonly newsletterBannerElement: NodeListOf<Element> = document.querySelectorAll('.js-newsletterBanner');
  private readonly footerElement: HTMLElement | null = document.querySelector('.js-footer');
  private readonly footerNewsletterElement: HTMLElement | null = document.querySelector('.js-footer-newsletter');
  private readonly loadingAnimationElement: HTMLElement | null = document.querySelector('.js-newsletterLoadingAnimation');
  private readonly errorMessageElement: HTMLElement | null = document.querySelector('.js-errorMessage');
  private readonly loadingClass: string = 'footer__newsletterStage--loading';
  private captchaReloadElement: HTMLElement | null = null;

  constructor() {
    if (this.toggleStagesElement && this.stage1Element && this.stage2Element) {
      this.fakeEmailElement?.reportValidity();
      if (this.fakeEmailElement) {
        if (checkMailValidity(this.fakeEmailElement)) {
          this.switchStage();
        }
      }
    }

    if (this.formElement) {
      this.fetchCaptchaImage();
    }

    /* cancel submit on fakeEmail element */
    if (this.fakeEmailElement) {
      this.fakeEmailElement.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      });
    }

    if (this.sendButtonElement && this.formElement) {
      this.formElement.addEventListener('submit', (event) => {
        const captchaResultElement: HTMLInputElement | null = document.querySelector('.js-captchaResult');
        event.preventDefault();
        const actionUrl = this.formElement?.action;

        if (this.firstnameElement
          && this.lastnameElement
          && this.emailElement
          && this.hpElement
          && this.captchaElement
          && this.countryElement
          && captchaResultElement) {
          // use different placeholders to not get detected as spam by SpamUtility
          const data = {
            firstname: this.firstnameElement.value,
            lastname: this.lastnameElement.value || 'nn',
            email: this.emailElement.value,
            hp: this.hpElement.value,
            captcha: this.captchaElement.value,
            country: this.countryElement.value,
            captchaResult: captchaResultElement.value,
          };

          if (checkMailValidity(this.emailElement)) {
            let loadingAnimation: LoadingAnimation | null = null;
            if (this.loadingAnimationElement && this.loadingAnimationElement.classList.contains('hide')) {
              loadingAnimation = new LoadingAnimation('.js-newsletterLoadingAnimation');
              loadingAnimation.showLoadingAnimation();
            }

            if (this.sendButtonElement) {
              this.sendButtonElement.classList.add('button--inactive');
              this.sendButtonElement.disabled = true;
            }

            this.stage2Element?.classList.add(this.loadingClass);

            this.sendRequest(actionUrl, data)
              .then((response) => {
                if (!response.isValid) {
                  this.stage2Element?.classList.remove(this.loadingClass);
                  if (this.errorMessageElement) {
                    this.errorMessageElement.innerText = response.message;
                  }
                  loadingAnimation?.hideLoadingAnimation();
                  if (this.sendButtonElement) {
                    this.sendButtonElement.classList.remove('button--inactive');
                    this.sendButtonElement.disabled = false;
                  }
                  if (this.captchaElement) {
                    this.captchaElement.value = '';
                  }
                  return '';
                }
                if (this.stage3Element && response.available) {
                  loadingAnimation?.hideLoadingAnimation();
                  this.stage2Element?.classList.remove(this.loadingClass);
                  if (this.successContentElement) {
                    const successLightbox = new GLightbox({
                      skin: 'clean glightbox--newsletter',
                      width: 'inital',
                      height: 'inital',
                      touchNavigation: false,
                      draggable: false,
                      elements: [
                        {
                          content: this.successContentElement.innerHTML,
                        },
                      ],
                    });
                    successLightbox.open();
                    this.footerNewsletterElement?.remove();
                  }
                } else if (typeof response.error === 'string') {
                  alert(response.error);
                }
              })
              .catch((error) => {
                /* temporarily display error for dev debugging */
                console.error('error: ', error);
              });
          }

        }
      });
    }

    if (this.newsletterBannerElement) {
      this.newsletterBannerElement.forEach((banner) => banner.addEventListener('click', () => {
        this.switchStage();
        if (this.footerElement) {
          scrollToElement(this.footerElement);
        }
      }));
    }

    const countryFromIp = document.querySelector('body')?.dataset.country || null;
    if (countryFromIp && this.countryElement) {
      this.countryElement.value = countryFromIp;
    }

    document.querySelector('.js-otherReasonsCheckbox')?.addEventListener('change', (event) => {
      const otherReasonsInput: HTMLTextAreaElement | null = document.querySelector('.js-otherReasons');
      if (otherReasonsInput) {
        otherReasonsInput.classList.toggle('hide');
      }
    });
  }

  async sendRequest(url, data) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response.json();
  }

  async fetchCaptchaImage(initial = false) {
    const captchaImageGenerationUrl = this.captchaImageElement?.dataset.url || '';
    try {
      let response = await fetch(captchaImageGenerationUrl);
      const image = await response.text();
      if (response.ok && image.includes('form__rowCaptcha')) {
        if (this.captchaImageElement) {
          let responseHtml = document.createElement('div');
          responseHtml.innerHTML = image.trim();
          const captchaHtml = responseHtml.querySelector('.form__rowCaptcha');
          if (!captchaHtml) {
            return;
          }
          this.captchaImageElement.innerHTML = captchaHtml.parentElement?.innerHTML || '';
          this.captchaReloadElement = document.querySelector('.js-captcha-reload-newsletter');
          this.initCaptchaReload();
        }
      } else {
        console.error('Error fetching captcha image');
      }
    } catch (error) {
      console.error(error);
    }
  }

  switchStage() {
    /* transfer the email value to the real form */
    if (this.fakeEmailElement && this.emailElement) {
      this.emailElement.value = this.fakeEmailElement.value;
    }
  }

  private initCaptchaReload() {
    if (this.captchaReloadElement) {
      this.captchaReloadElement.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await this.fetchCaptchaImage();
      });
    }
  }
}
